import { Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { EffectSearch } from "components/EffectSearch/EffectSearch";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { prepareSchemaConditionForAutocompleteVariables } from "components/Layout/Campaign/Common/CampaignRules/rulesHelpers";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field } from "formik";
import { useMessage } from "hooks/intl";
import { memo } from "react";
import {
  CampaignTriggers,
  CampaignTriggersUnion,
  CampaignTypeUnion,
  CampaignTypes,
  SelectedSchemaCondition,
} from "types/campaigns";
import {
  achievementBasedExpirationData,
  birthdayCampaignBasedExpirationData,
  customEventBasedVariables,
  eventExpirationTemplatesItems,
  internalEventBasedExpirationData,
  transactionBasedExpirationData,
} from "./data";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    neverExpireWrapper: {
      background: "#E4E4E4",
      borderRadius: 4,
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: "60px",
      padding: theme.spacing(0, 8),

      "& svg": {
        cursor: "pointer",
      },
    },
  }),
);

export const ExpirationSettings = memo(
  ({
    fieldName,
    trigger,
    selectedSchemaConditions,
    expirationStrategy,
  }: {
    fieldName: string;
    trigger: CampaignTriggersUnion;
    selectedSchemaConditions: SelectedSchemaCondition[];
    expirationStrategy?: "expression" | "never_expires";
  }) => {
    const { message } = useMessage();
    const classes = useStyles();

    const getDataByTrigger = (type: CampaignTypeUnion) => {
      if (type === CampaignTypes.TIME) {
        return birthdayCampaignBasedExpirationData;
      } else {
        switch (trigger) {
          case CampaignTriggers.TRANSACTION:
          case CampaignTriggers.RETURN_TRANSACTION:
            return transactionBasedExpirationData;
          case CampaignTriggers.INTERNAL_EVENT:
            return internalEventBasedExpirationData;
          case CampaignTriggers.CUSTOM_EVENT:
          case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
            return [
              { name: "templates", items: eventExpirationTemplatesItems },
              {
                name: "variables",
                items: [
                  ...customEventBasedVariables,
                  ...prepareSchemaConditionForAutocompleteVariables(
                    selectedSchemaConditions,
                    "datetime",
                  ),
                ],
              },
            ];
          case CampaignTriggers.ACHIEVEMENT:
            return achievementBasedExpirationData;
          default:
            return [];
        }
      }
    };

    const isExpirationStrategyNeverType =
      expirationStrategy === "never_expires";

    return (
      <Field>
        {({ form }: { form: any }) => {
          const data = getDataByTrigger(form.values.type);
          const { setFieldValue } = form;

          return (
            <Grid item xs={12}>
              {!isExpirationStrategyNeverType ? (
                <EffectSearch
                  name={`${fieldName}.unitsExpirationRule.expression`}
                  items={data}
                  showHint={false}
                  multiInput={false}
                  placeholder={message({ id: "chooseExpirationDateFormula" })}
                  onItemSelect={(item) => {
                    switch (item.code) {
                      case "never_expires":
                        setFieldValue(
                          `${fieldName}.unitsExpirationRule.expirationStrategy`,
                          item.code,
                        );
                        return setFieldValue(
                          `${fieldName}.unitsExpirationRule.expression`,
                          undefined,
                        );
                    }
                  }}
                />
              ) : (
                <div className={classes.neverExpireWrapper}>
                  <Typography>{message({ id: "neverExpires" })}</Typography>
                  <XCloseIcon
                    onClick={() =>
                      setFieldValue(
                        `${fieldName}.unitsExpirationRule.expirationStrategy`,
                        "expression",
                      )
                    }
                  />
                </div>
              )}
            </Grid>
          );
        }}
      </Field>
    );
  },
);
